/**
 * One square in a seat chart
 * @typedef {Object} Seat
 * @property {number} n seat number
 * @property {number} x
 * @property {number} y
 * @property {boolean} [isCenter] the middle seat (House seat 218)
 */

/**
 * @param {Seat[]} seats
 * @param {(n: number) => string | undefined} fill party class for each seat
 * @param {(n: number) => boolean | string} holdovers truthy for seats not up for election
 * @param {(n: number) => string} border outline class for each seat
 * @param {{ dem: number, gop: number } | undefined} netChanges seats flipped by each party
 * @param {number} centerX
 * @param {number} seatWidth
 */
export function assignTrendFills(
  seats,
  fill,
  holdovers,
  border,
  netChanges,
  centerX,
  seatWidth
) {
  const contested = seats.filter((seat) => !holdovers(seat.n));
  const counts = { dem: 0, gop: 0, other: 0 };

  const fills = new Map(
    seats.map((seat) => [
      seat.n,
      holdovers(seat.n) ? fill(seat.n) : 'undecided',
    ])
  );

  const borders = new Map(seats.map((seat) => [seat.n, border(seat.n)]));

  for (const seat of contested) {
    const party = fill(seat.n);

    if (party === 'dem' || party === 'gop' || party === 'other') {
      counts[party] += 1;
    }
  }

  const topY = Math.min(...seats.map((seat) => seat.y));
  const centerSeat = seats.find((seat) => seat.isCenter);

  const useHouseTopRowOrder =
    seats.length === 435 &&
    centerSeat !== undefined &&
    Math.abs(centerSeat.y - topY) < 0.1;

  let demOrder;
  let gopOrder;

  if (useHouseTopRowOrder) {
    const isTopRow = (/** @type {Seat} */ seat) => Math.abs(seat.y - topY) < 0.1;

    const bodySeats = seats.filter((seat) => !isTopRow(seat));

    // Match shifted top-row squares to the columns below.
    const columnX = new Map();

    for (const seat of seats) {
      if (seat.isCenter || !isTopRow(seat)) {
        columnX.set(seat.n, seat.x);
        continue;
      }

      const nearest = bodySeats.reduce((best, candidate) => {
        if (
          !best ||
          Math.abs(candidate.x - seat.x) < Math.abs(best.x - seat.x)
        ) {
          return candidate;
        }

        return best;
      }, /** @type {Seat | null} */ (null));

      columnX.set(seat.n, nearest ? nearest.x : seat.x);
    }

    const isLeftSide = (/** @type {Seat} */ seat) =>
      columnX.get(seat.n) + seatWidth / 2 < centerX;

    const leftSeats = contested.filter(
      (seat) => !seat.isCenter && isLeftSide(seat)
    );

    const rightSeats = contested.filter(
      (seat) => !seat.isCenter && !isLeftSide(seat)
    );

    const centerSeats = contested.filter((seat) => seat.isCenter);

    // OUTLINES
    // Preserve the previously agreed outline arrangement.
    const leftBorderOrder = [...leftSeats].sort(
      (a, b) => columnX.get(a.n) - columnX.get(b.n) || b.y - a.y || a.n - b.n
    );

    const innerColumnX = Math.max(
      ...leftSeats.map((seat) => columnX.get(seat.n))
    );

    const innerTopThree = leftSeats
      .filter((seat) => Math.abs(columnX.get(seat.n) - innerColumnX) < 0.1)
      .sort((a, b) => a.y - b.y)
      .slice(0, 3);

    const innerTopIds = new Set(innerTopThree.map((seat) => seat.n));

    const rightBorderOrder = [...rightSeats].sort(
      (a, b) => columnX.get(a.n) - columnX.get(b.n) || a.y - b.y || a.n - b.n
    );

    const borderOrder = [
      ...leftBorderOrder.filter((seat) => !innerTopIds.has(seat.n)),
      ...innerTopThree,
      ...centerSeats,
      ...rightBorderOrder,
    ];

    const originalBorders = [...contested]
      .sort((a, b) => a.n - b.n)
      .map((seat) => border(seat.n));

    borderOrder.forEach((seat, index) => {
      borders.set(seat.n, originalBorders[index]);
    });

    // FILLS
    // Democrats: left to right, top to bottom per column.
    const leftFillOrder = [...leftSeats].sort(
      (a, b) => columnX.get(a.n) - columnX.get(b.n) || a.y - b.y || a.n - b.n
    );

    // Republicans: right to left, top to bottom per column.
    const rightFillOrder = [...rightSeats].sort(
      (a, b) => columnX.get(b.n) - columnX.get(a.n) || a.y - b.y || a.n - b.n
    );

    // Both parties reach the center square at seat 218.
    // After crossing the center, continue in the same
    // horizontal direction, still top to bottom.
    demOrder = [
      ...leftFillOrder,
      ...centerSeats,
      ...[...rightSeats].sort(
        (a, b) => columnX.get(a.n) - columnX.get(b.n) || a.y - b.y || a.n - b.n
      ),
    ];

    gopOrder = [
      ...rightFillOrder,
      ...centerSeats,
      ...[...leftSeats].sort(
        (a, b) => columnX.get(b.n) - columnX.get(a.n) || a.y - b.y || a.n - b.n
      ),
    ];
  } else {
    const centerLast = (/** @type {Seat} */ a, /** @type {Seat} */ b) =>
      Number(Boolean(a.isCenter)) - Number(Boolean(b.isCenter));

    // Other charts: both parties also fill top to bottom.
    demOrder = [...contested].sort(
      (a, b) => centerLast(a, b) || a.x - b.x || a.y - b.y
    );

    gopOrder = [...contested].sort(
      (a, b) => centerLast(a, b) || b.x - a.x || a.y - b.y
    );
  }

  /** @param {string} party @param {number} count @param {Seat[]} order */
  function allocate(party, count, order) {
    let allocated = 0;

    for (const seat of order) {
      if (allocated >= count) break;
      if (fills.get(seat.n) !== 'undecided') continue;

      fills.set(seat.n, party);
      allocated += 1;
    }
  }

  allocate('dem', counts.dem, demOrder);
  allocate('gop', counts.gop, gopOrder);

  // Yellow follows the inner edge of the Republican block.
  allocate('other', counts.other, gopOrder);

  const demGains = Math.min(
    counts.dem,
    Math.max(0, netChanges?.dem ?? 0, -(netChanges?.gop ?? 0) - counts.other)
  );

  const gopGains = Math.min(
    counts.gop,
    Math.max(0, netChanges?.gop ?? 0, -(netChanges?.dem ?? 0))
  );

  const demSeats = demOrder.filter((seat) => fills.get(seat.n) === 'dem');

  const gopSeats = gopOrder.filter((seat) => fills.get(seat.n) === 'gop');

  const desiredBorders = new Map();

  for (const seat of demSeats) {
    desiredBorders.set(seat.n, 'is_dem');
  }

  for (const seat of gopSeats) {
    desiredBorders.set(seat.n, 'is_gop');
  }

  for (const seat of contested) {
    if (fills.get(seat.n) === 'other') {
      desiredBorders.set(seat.n, 'is_gop');
    }
  }

  // Flipped-seat outlines follow the end of each fill order.
  for (const seat of demSeats.slice(demSeats.length - demGains)) {
    desiredBorders.set(seat.n, 'is_gop');
  }

  for (const seat of gopSeats.slice(gopSeats.length - gopGains)) {
    desiredBorders.set(seat.n, 'is_dem');
  }

  // Swap outlines to preserve the total of each class.
  // Holdover seats never participate.
  for (const [seatNumber, desiredBorder] of desiredBorders) {
    // every seat has a border, so the fallback never applies
    const currentBorder = borders.get(seatNumber) ?? '';

    if (currentBorder === desiredBorder) continue;

    const donorOrder = desiredBorder === 'is_dem' ? gopOrder : demOrder;

    const donor = donorOrder.find(
      (seat) =>
        borders.get(seat.n) === desiredBorder &&
        desiredBorders.get(seat.n) !== desiredBorder
    );

    if (!donor) continue;

    borders.set(seatNumber, desiredBorder);
    borders.set(donor.n, currentBorder);
  }

  return { fills, borders };
}
